// Visitor geo + locale detection.
// Caches the result in localStorage for 7 days. Pure logic, no React.

const CACHE_KEY = 'sunify_geo';
const CACHE_TTL_MS = 7 * 24 * 60 * 60 * 1000; // 7 days

// ISO country code -> { language, currency, country_name (matches Checkout select) }
// language must be one of: sk, cs, de, en, ru
// currency must be one of: EUR, USD
// country_name must match an option in Checkout's country select (Slovakia, Czech Republic, Germany, Austria, Poland, Hungary)
const COUNTRY_MAP = {
  SK: { language: 'sk', currency: 'EUR', country_name: 'Slovakia' },
  CZ: { language: 'cs', currency: 'EUR', country_name: 'Czech Republic' },
  DE: { language: 'de', currency: 'EUR', country_name: 'Germany' },
  AT: { language: 'de', currency: 'EUR', country_name: 'Austria' },
  CH: { language: 'de', currency: 'EUR', country_name: null },
  LI: { language: 'de', currency: 'EUR', country_name: null },
  PL: { language: 'en', currency: 'EUR', country_name: 'Poland' },
  HU: { language: 'en', currency: 'EUR', country_name: 'Hungary' },
  RU: { language: 'ru', currency: 'EUR', country_name: null },
  UA: { language: 'ru', currency: 'EUR', country_name: null },
  BY: { language: 'ru', currency: 'EUR', country_name: null },
  KZ: { language: 'ru', currency: 'EUR', country_name: null },
  US: { language: 'en', currency: 'USD', country_name: null },
  CA: { language: 'en', currency: 'USD', country_name: null },
  GB: { language: 'en', currency: 'EUR', country_name: null },
  IE: { language: 'en', currency: 'EUR', country_name: null },
  AU: { language: 'en', currency: 'USD', country_name: null },
  NZ: { language: 'en', currency: 'USD', country_name: null },
};

const DEFAULT_PROFILE = { language: 'en', currency: 'EUR', country_name: null };

// Languages the Sunify UI actually supports (must match i18n/config.js supportedLngs).
export const SUPPORTED_LANGS = ['sk', 'cs', 'de', 'en', 'ru'];

/** Primary language tag ("ru-RU" -> "ru"). Returns null if none. */
export const getNavigatorPrimaryLang = () => {
  if (typeof navigator === 'undefined') return null;
  const raw = (navigator.languages && navigator.languages[0]) || navigator.language || '';
  if (!raw) return null;
  return String(raw).toLowerCase().split('-')[0];
};

/** True when the visitor's browser/OS primary language is one we ship translations for. */
export const isNavigatorLangSupported = () => {
  const lang = getNavigatorPrimaryLang();
  return !!(lang && SUPPORTED_LANGS.includes(lang));
};

/** Read ?lang=xx from current URL. Returns a supported language or null. */
export const getUrlLangParam = () => {
  try {
    if (typeof window === 'undefined') return null;
    const sp = new URLSearchParams(window.location.search);
    const v = (sp.get('lang') || '').toLowerCase().trim();
    return SUPPORTED_LANGS.includes(v) ? v : null;
  } catch {
    return null;
  }
};

/** Read cached geo if not expired. Returns null otherwise. */
const readCache = () => {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    const obj = JSON.parse(raw);
    if (!obj || !obj.ts) return null;
    if (Date.now() - obj.ts > CACHE_TTL_MS) return null;
    return obj.data;
  } catch {
    return null;
  }
};

const writeCache = (data) => {
  try {
    localStorage.setItem(CACHE_KEY, JSON.stringify({ ts: Date.now(), data }));
  } catch {
    /* ignore quota / private mode */
  }
};

/** Map a navigator language string ("ru-RU", "de", "cs-CZ") to a profile.
 *  Returns null if no useful match. */
const profileFromNavigator = () => {
  const langs = (typeof navigator !== 'undefined' && navigator.languages) || [];
  const primary = langs[0] || (typeof navigator !== 'undefined' && navigator.language) || '';
  if (!primary) return null;
  const [lang, region] = primary.toLowerCase().split('-');
  if (region) {
    const code = region.toUpperCase();
    if (COUNTRY_MAP[code]) {
      return { country_code: code, source: 'navigator', ...COUNTRY_MAP[code] };
    }
  }
  // Fallback: language-only mapping
  const langMap = {
    sk: { country_code: 'SK', ...COUNTRY_MAP.SK },
    cs: { country_code: 'CZ', ...COUNTRY_MAP.CZ },
    de: { country_code: 'DE', ...COUNTRY_MAP.DE },
    ru: { country_code: 'RU', ...COUNTRY_MAP.RU },
    en: { country_code: 'GB', ...COUNTRY_MAP.GB },
  };
  if (langMap[lang]) {
    return { source: 'navigator', ...langMap[lang] };
  }
  return null;
};

/** Detect via IP using ipwho.is (free, no API key, CORS enabled). */
const profileFromIp = async () => {
  try {
    const ctrl = new AbortController();
    const tid = setTimeout(() => ctrl.abort(), 3500);
    const res = await fetch('https://ipwho.is/?fields=country_code,country,success', { signal: ctrl.signal });
    clearTimeout(tid);
    if (!res.ok) return null;
    const data = await res.json();
    if (!data || data.success === false) return null;
    const code = (data.country_code || '').toUpperCase();
    if (!code) return null;
    const profile = COUNTRY_MAP[code] || DEFAULT_PROFILE;
    return {
      country_code: code,
      country_full_name: data.country || null,
      source: 'ip',
      ...profile,
    };
  } catch {
    return null;
  }
};

/** Detect visitor geo profile. Returns a merged profile object.
 *  Always resolves (never throws). */
export const detectGeo = async () => {
  const cached = readCache();
  if (cached) return cached;

  let profile = await profileFromIp();
  if (!profile) profile = profileFromNavigator();
  if (!profile) profile = { country_code: null, source: 'default', ...DEFAULT_PROFILE };

  writeCache(profile);
  return profile;
};

/** Sync helper: returns whatever is cached without doing a network call. */
export const getCachedGeo = () => readCache();

/** Did the user manually pick a language (via the header switcher)? */
export const isLangUserChosen = () => {
  try { return localStorage.getItem('sunify_lang_user_chosen') === '1'; } catch { return false; }
};

/** Did the user manually pick a currency (via the header switcher)? */
export const isCurrencyUserChosen = () => {
  try { return localStorage.getItem('sunify_currency_user_chosen') === '1'; } catch { return false; }
};

export const markLangUserChosen = () => {
  try { localStorage.setItem('sunify_lang_user_chosen', '1'); } catch { /* ignore */ }
};

export const markCurrencyUserChosen = () => {
  try { localStorage.setItem('sunify_currency_user_chosen', '1'); } catch { /* ignore */ }
};
